import { Avatar, Heading } from 'copilot-design-system';
import React from 'react';

import { UserCompanyAvatar } from 'src/components/UserCompanyAvatar';
import { CompanyFields, UserFields } from 'src/constants';
import { FlagsContext, PortalConfigContext } from 'src/context';
import { Contract } from 'src/entities/Contract';
import { useAppSelector } from 'src/hooks/useStore';
import { UserAvatar } from 'src/legacy/components/User';
import { arrayToMap } from 'src/utils/array';

type ContractDataWithRecipient = Contract & {
  recipientData: {
    client?: UserFields;
    company?: CompanyFields;
  };
};
type Props = {
  data: ContractDataWithRecipient;
};

const LegacyContractClientRenderer = React.memo(({ data }: Props) => {
  const clients = useAppSelector((state) =>
    arrayToMap(state.clients.clients, 'id'),
  );
  const recipient = clients.get(data.recipientId ?? '');

  if (!recipient || !recipient.fields) {
    return null;
  }

  return (
    <UserAvatar
      avatarUrl={recipient.fields.avatarImageUrl || ''}
      name={`${recipient.fields.givenName} ${recipient.fields.familyName}`}
      description={recipient.fields.email}
      fallbackColor={recipient.fields.fallbackColor}
      primaryTextVariant="tableMain"
      shape="circle"
    />
  );
});

const NewContractClientRenderer = ({ data }: Props) => {
  const portalConfig = React.useContext(PortalConfigContext);
  const areCompaniesEnabled = !portalConfig?.features.disableCompanies;

  const { recipientData } = data;
  if (!recipientData) {
    return null;
  }

  // If we have both client and company, use UserCompanyAvatar
  if (recipientData.client && recipientData.company) {
    const shouldShowCompanyInfo =
      areCompaniesEnabled && (recipientData.client.companyIds?.length ?? 0) > 1;

    const userData = {
      name: [
        recipientData.client.givenName,
        recipientData.client.familyName,
      ].join(' '),
      src: recipientData.client.avatarImageUrl,
      fallbackColor: recipientData.client.fallbackColor,
      companyAvatar: shouldShowCompanyInfo
        ? {
            name: recipientData.company.name,
            src: recipientData.company.avatarImageURL ?? '',
            fallbackColor: recipientData.company.fallbackColor ?? '#6B7280', // fallback to gray
          }
        : undefined,
    };

    return (
      <div className="flex items-center gap-3 p-2">
        <div className="flex-none w-[34px] h-[34px] flex items-center justify-center">
          <UserCompanyAvatar user={userData} size={32} />
        </div>
        <div>
          <Heading size="xs">
            {[
              recipientData.client.givenName,
              recipientData.client.familyName,
            ].join(' ')}
            {shouldShowCompanyInfo && (
              <span className="text-gray-500">
                {' '}
                - {recipientData.company.name}
              </span>
            )}
          </Heading>
          <div className="text-xs text-gray-500">
            {recipientData.client.email}
          </div>
        </div>
      </div>
    );
  }

  // If we only have one (client or company), use single Avatar
  const singleEntity = recipientData.client || recipientData.company;
  if (!singleEntity) return null;

  return (
    <div className="flex items-center gap-3 p-2">
      <div className="flex-none w-[34px] h-[34px] flex items-center justify-center">
        <Avatar
          alt={
            recipientData.client
              ? [
                  recipientData.client.givenName,
                  recipientData.client.familyName,
                ].join(' ')
              : recipientData.company?.name
          }
          src={
            recipientData.client
              ? recipientData.client.avatarImageUrl
              : (recipientData.company!.avatarImageURL ?? '')
          }
          fallbackColor={recipientData.client?.fallbackColor}
          variant={recipientData.client ? 'circle' : 'rounded'}
          size="md"
          text={
            recipientData.client
              ? recipientData.client.givenName.charAt(0).toUpperCase()
              : recipientData.company!.name.charAt(0).toUpperCase()
          }
        />
      </div>
      <div>
        <Heading size="xs">
          {recipientData.client
            ? [
                recipientData.client.givenName,
                recipientData.client.familyName,
              ].join(' ')
            : recipientData.company?.name}
        </Heading>
        {recipientData.client && (
          <div className="text-xs text-gray-500">
            {recipientData.client.email}
          </div>
        )}
      </div>
    </div>
  );
};

export const ContractClientRenderer = ({ data }: Props) => {
  const { EnableManyCompanies } = React.useContext(FlagsContext);
  if (EnableManyCompanies) {
    return <NewContractClientRenderer data={data} />;
  }
  return <LegacyContractClientRenderer data={data} />;
};
